import { detectOldSystemExport, legacyCsvTable } from './legacyImport'

const SUPPORTED_HEADERS = new Set(['first_name', 'last_name', 'email', 'mobile', 'landline', 'notes'])
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const clean = value => String(value ?? '').replace(/\s+/g, ' ').trim()
const contact = (value, isPhone = false) => {
  const text = clean(value)
  if (text === '-' || text === "'-") return ''
  return isPhone && /^'(?=\+?\d)/.test(text) ? text.slice(1) : text
}
const emailKey = value => clean(value).toLowerCase()
const phoneKey = value => clean(value).replace(/\D/g, '')
const nameKey = customer => `${clean(customer.first_name).toLowerCase()} ${clean(customer.last_name).toLowerCase()}`.trim()
const sharesContact = (a, b) => (
  (emailKey(a.email) && emailKey(a.email) === emailKey(b.email))
  || (phoneKey(a.phone) && phoneKey(a.phone) === phoneKey(b.phone))
)
const conflictingIdentity = (a, b) => (
  nameKey(a) !== nameKey(b)
  || (emailKey(a.email) && emailKey(b.email) && emailKey(a.email) !== emailKey(b.email))
  || (phoneKey(a.phone) && phoneKey(b.phone) && phoneKey(a.phone) !== phoneKey(b.phone))
)

export function parseOldSystemCustomers(text) {
  if (detectOldSystemExport(text) === 'historic_jobs') {
    return { rows: [], unsupportedColumns: [], errors: ['This is a historical job export, not a customer file.'] }
  }
  const table = legacyCsvTable(text)
  const { headers, rows } = table
  if (!['first_name', 'last_name'].every(header => headers.includes(header))
    || !['email', 'mobile', 'landline'].some(header => headers.includes(header))) {
    return { rows: [], unsupportedColumns: [], errors: ['Customer files need First Name, Last Name, and Email or Phone columns.'] }
  }
  if (!rows.length) return { rows: [], unsupportedColumns: [], errors: ['CSV needs at least one customer row.'] }
  const unnamed = headers.map((header, index) => header ? -1 : index).filter(index => index >= 0)
  if (unnamed.length && headers.slice(unnamed[0] + 1).some(Boolean)) {
    return { rows: [], unsupportedColumns: [], errors: [`Column ${unnamed[0] + 1} has no heading before another named column.`] }
  }
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const unnamedIndex = unnamed.find(index => clean(rows[rowIndex][index]))
    if (unnamedIndex !== undefined) {
      return { rows: [], unsupportedColumns: [], errors: [`Row ${rowIndex + 2}, column ${unnamedIndex + 1} has data under an unnamed heading.`] }
    }
    const extraIndex = rows[rowIndex].findIndex((value, index) => index >= headers.length && clean(value))
    if (extraIndex >= 0) {
      return { rows: [], unsupportedColumns: [], errors: [`Row ${rowIndex + 2}, column ${extraIndex + 1} has data without a heading.`] }
    }
  }
  const named = headers.filter(Boolean)
  if (named.length !== new Set(named).size) {
    return { rows: [], unsupportedColumns: [], errors: ['Duplicate CSV column names must be resolved before preview.'] }
  }

  const unsupportedColumns = headers.filter(header => header && !SUPPORTED_HEADERS.has(header))
  const parsedRows = rows.map((values, index) => {
    const raw = Object.fromEntries(headers.map((header, position) => [header, clean(values[position])]))
    const customer = {
      first_name: raw.first_name || '',
      last_name: raw.last_name || '',
      email: contact(raw.email),
      phone: contact(raw.mobile, true) || contact(raw.landline, true),
      notes: raw.notes || '',
    }
    const errors = []
    const warnings = contact(raw.mobile, true) && contact(raw.landline, true) ? ['Landline is ignored because Mobile is present.'] : []
    if (values.length > headers.length && values.slice(headers.length).some(clean)) errors.push('Extra CSV cells need review.')
    if (!customer.first_name || !customer.last_name) errors.push('First and last name are required.')
    if (customer.first_name.length > 120 || customer.last_name.length > 120) errors.push('Name is too long.')
    if (customer.email && (customer.email.length > 254 || !EMAIL_RE.test(customer.email))) errors.push('Email address is invalid.')
    if (customer.phone && (phoneKey(customer.phone).length < 7 || phoneKey(customer.phone).length > 16)) errors.push('Contact number is invalid.')
    if (!customer.email && !customer.phone) errors.push('Email or contact number is required for safe matching.')
    if (customer.notes.length > 4000) errors.push('Notes are too long.')
    return { rowNumber: table.rowNumbers?.[index] || index + 2, customer, errors, warnings }
  })
  return { rows: parsedRows, unsupportedColumns, errors: [] }
}

export function reviewOldSystemCustomers(rows, existingCustomers, skippedRowNumbers = []) {
  const skipped = new Set(skippedRowNumbers)
  return rows.map((row, index) => {
    const candidates = (existingCustomers || []).filter(existing => sharesContact(row.customer, existing))
    const sourcePeers = rows.filter(other => other.rowNumber !== row.rowNumber && sharesContact(row.customer, other.customer))
    const prior = rows.slice(0, index).filter(other => sharesContact(row.customer, other.customer))
    const sameNameOnly = (existingCustomers || []).filter(existing =>
      nameKey(existing) === nameKey(row.customer) && !sharesContact(row.customer, existing)
    )
    const sameNameSource = rows.filter(other =>
      other.rowNumber !== row.rowNumber && nameKey(other.customer) === nameKey(row.customer)
        && !sharesContact(row.customer, other.customer)
    )
    const ambiguous = candidates.length > 1
      || candidates.some(existing => conflictingIdentity(row.customer, existing))
      || sourcePeers.some(other => conflictingIdentity(row.customer, other.customer))
    const errors = [...row.errors]
    if (ambiguous && !skipped.has(row.rowNumber)) errors.push('Ambiguous contact identity; explicitly Skip this row or resolve it separately.')
    const warnings = [...row.warnings]
    if (sameNameOnly.length || sameNameSource.length) warnings.push('Same name with different contacts; not automatically matched.')
    return {
      ...row,
      errors,
      validationErrors: row.errors,
      warnings,
      ambiguous,
      candidates,
      priorRows: prior.map(other => other.rowNumber),
      similarNames: sameNameOnly,
      similarSourceRows: sameNameSource.map(other => other.rowNumber),
      match: candidates.length === 1 && !ambiguous ? candidates[0] : null,
      action: errors.length ? 'blocked' : ambiguous && skipped.has(row.rowNumber) ? 'skip'
        : candidates.length || prior.length ? 'reuse' : 'create',
    }
  })
}

export function oldSystemCustomerPayload(rows) {
  return rows.map(row => ({ row_number: row.rowNumber, ...row.customer, decision: row.action === 'skip' ? 'skip' : 'import' }))
}
